// ============================================================
// TOXIC, DRAWN (plan body-map T2; engine/plate/auras `toxicLook`; the
// user's three together, 2026-10-09). A wave of poison through the whole
// body from the heart with every beat (its lines re-drawn green under a
// ragged mask whose rings beat out on tracks); its natural weapons' veins
// pulsing up from the hands, clipped to the arms; and where it is struck,
// the poison bursts through it from that place and ebbs (one-shot CSS, in
// the hit's turn). Its blood drains green: the plate sets `--plate-blood`
// (./TraitLayers).
// ============================================================

import type { CSSProperties } from 'react';
import { toxicLook } from '../../../../engine/plate/auras';
import { landed } from '../../../../engine/plate/burst';
import { anchorOf, type PlateFrame, type Point } from '../../../../engine/plate/frame';

type Placed = Pick<PlateFrame, 'anchors' | 'box'>;
import { piecePoints } from '../../../../engine/plate/figureArt';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';
import type { HitPlay } from '../../usePlateHits';
import { MASK_RECT } from '../conditions';
import { timed } from '../timing';

/** The poison's ragged edge: the mask's rings thrown out of round. */
function Ragged({ id, seed }: { id: string; seed: number }) {
  return (
    <filter id={id} x="-30%" y="-30%" width="160%" height="160%">
      <feTurbulence type="turbulence" baseFrequency={0.016} numOctaves={3} seed={seed} result="n" />
      <feDisplacementMap in="SourceGraphic" in2="n" scale={200} xChannelSelector="R" yChannelSelector="G" />
    </filter>
  );
}

/** A soft white disc for a mask: full at its middle, fading out. */
export function SoftDisc({ id, from = 0.3 }: { id: string; from?: number }) {
  return (
    <radialGradient id={id}>
      <stop offset={from} stopColor="white" />
      <stop offset={1} stopColor="white" stopOpacity={0} />
    </radialGradient>
  );
}

/** The heart's beat: its resting glow, and the wave out with every beat, through a ragged mask. */
export function ToxicDefs({ u, x, heart: [hx, hy] }: { u: string; x: number; heart: Point }) {
  const { rest, wave } = toxicLook(x);
  return (
    <>
      <SoftDisc id={`${u}-vsg`} />
      <SoftDisc id={`${u}-thg`} from={0.35} />
      <Ragged id={`${u}-vsf`} seed={5} />
      <Ragged id={`${u}-vsf2`} seed={9} />
      <mask id={`${u}-vsm`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
        <g filter={`url(#${u}-vsf)`}>
          <circle cx={hx} cy={hy} r={rest} fill={`url(#${u}-vsg)`} />
          <circle cx={hx} cy={hy} fill={`url(#${u}-vsg)`} {...tracked([wave.r, wave.opacity])} />
        </g>
      </mask>
    </>
  );
}

/** The figure's lines re-drawn green and heavy under a mask - the poison's look wherever it reaches. */
function Poisoned({
  u,
  mask,
  className,
  style,
}: {
  u: string;
  mask: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <g className={className ?? tx.toxicLines} mask={`url(#${mask})`} filter={`url(#${u}-tglow)`} style={style}>
      <use href={`#${u}-art`} />
    </g>
  );
}

/** The arms' outline pieces and their height: where the fists' veins climb. */
export interface Arms {
  pieces: string[];
  top: number;
  bottom: number;
}

/** Whose veins: Toxic's in its green, Deadly Natural Weapons' the same pulse in blood red (ids and lines their own). */
const FISTS = {
  toxic: { id: 'tv', lines: tx.toxicLines },
  dnw: { id: 'dnw', lines: tx.dnwLines },
};

/** Its natural weapons' veins: from the hands up most of each arm and back, pulsing - clipped to the arms. */
export function Fists({ u, arms, kind }: { u: string; arms: Arms; kind: keyof typeof FISTS }) {
  const { id, lines: className } = FISTS[kind];
  const { veins } = toxicLook(1);
  return (
    <>
      <linearGradient id={`${u}-${id}g`} gradientUnits="userSpaceOnUse" x1={0} y1={arms.top} x2={0} y2={arms.bottom}>
        <stop stopColor="black" {...tracked([veins.dark])} />
        <stop stopColor="white" {...tracked([veins.light])} />
      </linearGradient>
      <filter id={`${u}-${id}f`} x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="turbulence" baseFrequency={0.018} numOctaves={3} seed={11} result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale={140} xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <mask id={`${u}-${id}m`} maskUnits="userSpaceOnUse" {...MASK_RECT}>
        <rect
          x={-500}
          y={arms.top - 200}
          width={3200}
          height={arms.bottom - arms.top + 400}
          fill={`url(#${u}-${id}g)`}
          filter={`url(#${u}-${id}f)`}
        />
      </mask>
      <clipPath id={`${u}-${id}c`}>
        {arms.pieces.map((p, i) => (
          <polygon key={i} points={piecePoints(p).join(' ')} />
        ))}
      </clipPath>
      <g clipPath={`url(#${u}-${id}c)`} {...tracked([veins.pulse])}>
        <Poisoned u={u} mask={`${u}-${id}m`} className={className} />
      </g>
    </>
  );
}

/** Where it is struck the poison bursts through it from that place, and ebbs - each landed hit, in its turn. */
function Bursts({ u, play, place }: { u: string; play: HitPlay; place: Placed }) {
  return landed(play.burst.hits).map((h, i) => {
    const [x, y] = anchorOf(place, h.location);
    const id = `${u}-thm${i}`;
    return (
      <g key={`${play.id}:${i}`}>
        <mask id={id} maskUnits="userSpaceOnUse" {...MASK_RECT}>
          <circle
            className={tx.toxicSpread}
            cx={x}
            cy={y}
            r={80}
            fill={`url(#${u}-thg)`}
            filter={`url(#${u}-vsf2)`}
            style={timed(h.at)}
          />
        </mask>
        <Poisoned u={u} mask={id} className={tx.toxicBurst} style={timed(h.at)} />
      </g>
    );
  });
}

interface FrontProps {
  u: string;
  /** The heartbeat's poison - and the arms when it has natural weapons. */
  arms: Arms | null;
  play: HitPlay | null;
  /** Where the figure is struck: its anchors and bounds. */
  place: Placed;
}

/** Before the figure: the poisoned lines the heartbeat reaches, the fists' veins, and a hit's burst. */
export function ToxicFront({ u, arms, play, place }: FrontProps) {
  return (
    <g pointerEvents="none" data-trait="toxic">
      <Poisoned u={u} mask={`${u}-vsm`} />
      {arms && <Fists u={u} arms={arms} kind="toxic" />}
      {play && <Bursts u={u} play={play} place={place} />}
    </g>
  );
}
